import React, { useEffect, useState, useCallback } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdPayment, MdCheckCircle, MdError, MdHourglassEmpty } from 'react-icons/md';

interface PaymentLog {
    _id: string;
    sellerId: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
    };
    buyerId?: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
    };
    leadId?: {
        _id: string;
        orderNumber: string;
        email: string;
        status: string;
    };
    purchaseOrderId?: {
        _id: string;
        title: string;
        status: string;
    };
    amount: number;
    buyerPrice?: number;
    commission?: number;
    stripeTransferId?: string;
    stripeAccountId: string;
    status: 'pending' | 'success' | 'failed';
    errorMessage?: string;
    paymentType: 'marketplace' | 'purchase_order' | 'direct_offer';
    createdAt: string;
    updatedAt: string;
}

interface PaymentStats {
    totalPayments: number;
    successfulPayments: number;
    failedPayments: number;
    pendingPayments: number;
    totalAmountPaid: number;
    totalCommission: number;
    recentPayments: number;
}

export default function BackOfficePaymentLogs() {
    const [payments, setPayments] = useState<PaymentLog[]>([]);
    const [stats, setStats] = useState<PaymentStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [filterType, setFilterType] = useState<string>('');

    const fetchPayments = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: '50',
            });
            
            if (filterStatus) params.append('status', filterStatus);
            if (filterType) params.append('paymentType', filterType);

            const response = await axios.get(
                `${CONFIG.apiUrl}/payment-log?${params.toString()}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setPayments(response.data.payments);
            setTotalPages(response.data.pagination.totalPages);
            setTotalCount(response.data.pagination.totalCount);
        } catch (error) {
            console.error('Error fetching payment logs:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, filterStatus, filterType]);

    useEffect(() => {
        fetchPayments();
        fetchStats();
    }, [fetchPayments]);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/payment-log/stats/summary`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching payment stats:', error);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <MdCheckCircle className="h-5 w-5 text-green-500" />;
            case 'failed':
                return <MdError className="h-5 w-5 text-red-500" />;
            case 'pending':
                return <MdHourglassEmpty className="h-5 w-5 text-yellow-500" />;
            default:
                return null;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'success':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'failed':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getPaymentTypeColor = (type: string) => {
        switch (type) {
            case 'purchase_order':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'marketplace':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
            case 'direct_offer':
                return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const formatPaymentType = (type: string) => {
        return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    return (
        <div className="pt-[50px]">
            {/* Statistics Cards */}
            {stats && (
                <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                    <Card extra="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Total Payments
                                </p>
                                <p className="text-2xl font-bold text-navy-700 dark:text-white">
                                    {stats.totalPayments.toLocaleString()}
                                </p>
                            </div>
                            <MdPayment className="h-10 w-10 text-brand-500" />
                        </div>
                    </Card>

                    <Card extra="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Successful
                                </p>
                                <p className="text-2xl font-bold text-green-500">
                                    {stats.successfulPayments.toLocaleString()}
                                </p>
                            </div>
                            <MdCheckCircle className="h-10 w-10 text-green-500" />
                        </div>
                    </Card>

                    <Card extra="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Total Amount Paid
                                </p>
                                <p className="text-2xl font-bold text-navy-700 dark:text-white">
                                    {stats.totalAmountPaid.toFixed(2)}€
                                </p>
                            </div>
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                                <span className="text-lg font-bold text-green-500">€</span>
                            </div>
                        </div>
                    </Card>

                    <Card extra="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Total Commission
                                </p>
                                <p className="text-2xl font-bold text-brand-500">
                                    {stats.totalCommission.toFixed(2)}€
                                </p>
                            </div>
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900">
                                <span className="text-lg font-bold text-brand-500">€</span>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* Main Table Card */}
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            Payment Logs
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            All seller payment transactions - {totalCount.toLocaleString()} total
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-4 flex gap-3">
                    <select
                        value={filterStatus}
                        onChange={(e) => {
                            setFilterStatus(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    >
                        <option value="">All Status</option>
                        <option value="success">Success</option>
                        <option value="failed">Failed</option>
                        <option value="pending">Pending</option>
                    </select>

                    <select
                        value={filterType}
                        onChange={(e) => {
                            setFilterType(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    >
                        <option value="">All Types</option>
                        <option value="purchase_order">Purchase Order</option>
                        <option value="marketplace">Marketplace</option>
                        <option value="direct_offer">Direct Offer</option>
                    </select>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="py-8 text-center text-gray-600 dark:text-gray-400">
                        Loading payments...
                    </div>
                ) : payments.length === 0 ? (
                    <div className="py-8 text-center text-gray-600 dark:text-gray-400">
                        No payments found
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-navy-600">
                                        <th className="pb-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                            Date
                                        </th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                            Seller
                                        </th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                            Buyer
                                        </th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                            Type
                                        </th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                            Amount
                                        </th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                            Commission
                                        </th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                            Status
                                        </th>
                                        <th className="pb-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                            Stripe ID
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map((payment) => (
                                        <tr
                                            key={payment._id}
                                            className="border-b border-gray-100 dark:border-navy-700"
                                        >
                                            <td className="py-3 text-sm text-gray-700 dark:text-gray-300">
                                                {new Date(payment.createdAt).toLocaleDateString('fr-FR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </td>
                                            <td className="py-3 text-sm">
                                                <div className="font-medium text-navy-700 dark:text-white">
                                                    {payment.sellerId.firstName} {payment.sellerId.lastName}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {payment.sellerId.email}
                                                </div>
                                            </td>
                                            <td className="py-3 text-sm">
                                                {payment.buyerId ? (
                                                    <>
                                                        <div className="font-medium text-navy-700 dark:text-white">
                                                            {payment.buyerId.firstName} {payment.buyerId.lastName}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {payment.buyerId.email}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="py-3 text-sm">
                                                <span
                                                    className={`rounded-full px-2 py-1 text-xs font-medium ${getPaymentTypeColor(
                                                        payment.paymentType
                                                    )}`}
                                                >
                                                    {formatPaymentType(payment.paymentType)}
                                                </span>
                                            </td>
                                            <td className="py-3 text-sm font-semibold text-navy-700 dark:text-white">
                                                {payment.amount.toFixed(2)}€
                                            </td>
                                            <td className="py-3 text-sm text-brand-500">
                                                {payment.commission ? `${payment.commission.toFixed(2)}€` : '-'}
                                            </td>
                                            <td className="py-3 text-sm">
                                                <div className="flex items-center gap-2">
                                                    {getStatusIcon(payment.status)}
                                                    <span
                                                        className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(
                                                            payment.status
                                                        )}`}
                                                    >
                                                        {payment.status.toUpperCase()}
                                                    </span>
                                                </div>
                                                {payment.errorMessage && (
                                                    <div className="mt-1 text-xs text-red-500">
                                                        {payment.errorMessage}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-3 text-xs text-gray-500">
                                                {payment.stripeTransferId ? (
                                                    <code className="rounded bg-gray-100 px-1 py-0.5 dark:bg-navy-700">
                                                        {payment.stripeTransferId}
                                                    </code>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-5 flex items-center justify-center gap-2">
                                <button
                                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                                >
                                    Previous
                                </button>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    onClick={() =>
                                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                                    }
                                    disabled={currentPage === totalPages}
                                    className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
}
